import { getDiscountFromCoupon } from '../utils/discountUtils';
import { selectBestCoupon } from '../utils/coupons';
import './PeptideCard.css';

export interface VendorProduct {
  id: number;
  vendor_id: string;
  vendor_name: string;
  canonical_peptide: string | null;
  peptide_name_raw: string | null;
  product_name: string;
  product_url: string;
  price: number;
  currency: string;
  vial_size_text: string | null;
  vial_size_mg: number | null;
  price_per_mg: number | null;
  coupon_code: string | null;
  coupon_description: string | null;
  scraped_coupon_code: string | null;
  scraped_coupon_discount: string | null;
  vendor_website_url: string | null;
  vendor_logo_url: string | null;
  affiliate_url: string | null;
}

interface PeptideCardProps {
  product: VendorProduct;
  onPress: () => void;
  isBestPrice?: boolean;
  className?: string;
}

/**
 * SEO-optimized peptide card component
 */
export function PeptideCard({
  product,
  onPress,
  isBestPrice = false,
  className = '',
}: PeptideCardProps) {
  // Determine peptide name
  const peptideName = product.canonical_peptide || product.peptide_name_raw || product.product_name;
  
  // Select the best coupon (affiliate vs scraped)
  const activeCoupon = selectBestCoupon({
    coupon_code: product.coupon_code,
    coupon_description: product.coupon_description,
    scraped_coupon_code: product.scraped_coupon_code,
    scraped_coupon_discount: product.scraped_coupon_discount,
  });
  
  // Calculate discount if coupon is available
  const discount = getDiscountFromCoupon(
    product.price,
    product.vial_size_mg,
    activeCoupon.description
  );

  // Determine which prices to display (discounted if available, otherwise original)
  const displayPrice = discount?.discountedPrice ?? product.price;
  const displayPricePerMg = discount?.discountedPricePerMg ?? product.price_per_mg;

  // Format display price
  const formattedPrice = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: product.currency || 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(displayPrice);

  // Format original price (for strike-through display)
  const formattedOriginalPrice = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: product.currency || 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(product.price);

  // Format price per mg if available
  const formattedPricePerMg = displayPricePerMg
    ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      }).format(displayPricePerMg)
    : null;

  const hasDiscount = discount !== null;
  const productUrl = product.affiliate_url || product.product_url;

  // Get vendor initials for placeholder
  const getInitials = (name: string): string => {
    const words = name.trim().split(/\s+/);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <article className={`peptide-card ${isBestPrice ? 'peptide-card-best-price' : ''} ${className}`}>
      <div className="peptide-card-content">
        <header className="peptide-card-header">
          <h3 className="peptide-card-title">
            <a
              href={productUrl}
              onClick={(e) => {
                e.preventDefault();
                onPress();
              }}
              className="peptide-card-link"
            >
              {peptideName}
            </a>
          </h3>
          {isBestPrice && (
            <span className="peptide-card-badge" aria-label="Best price">
              Best Price
            </span>
          )}
        </header>

        <div className="peptide-card-vendor">
          {product.vendor_logo_url ? (
            <img
              src={product.vendor_logo_url}
              alt={`${product.vendor_name} logo`}
              className="peptide-card-vendor-logo"
              loading="lazy"
            />
          ) : (
            <div className="peptide-card-vendor-logo-placeholder" aria-label={`${product.vendor_name} logo placeholder`}>
              {getInitials(product.vendor_name)}
            </div>
          )}
          <span className="peptide-card-vendor-name">{product.vendor_name}</span>
        </div>

        {product.vial_size_text && (
          <div className="peptide-card-size">
            <span className="peptide-card-size-label">Size:</span>
            <span className="peptide-card-size-value">{product.vial_size_text}</span>
          </div>
        )}

        <div className="peptide-card-pricing">
          <div className="peptide-card-price-row">
            <span className="peptide-card-price-label">Price:</span>
            <div className="peptide-card-price-value">
              {hasDiscount && (
                <span className="peptide-card-price-original" aria-label={`Original price ${formattedOriginalPrice}`}>
                  {formattedOriginalPrice}
                </span>
              )}
              <span className="peptide-card-price-current" aria-label={`Current price ${formattedPrice}`}>
                {formattedPrice}
              </span>
              {hasDiscount && discount && (
                <span className="peptide-card-discount-badge" aria-label={`${discount.discountPercent}% discount`}>
                  {discount.discountPercent}% off
                </span>
              )}
            </div>
          </div>

          {formattedPricePerMg && (
            <div className="peptide-card-price-per-mg">
              <span className="peptide-card-price-per-mg-label">Per mg:</span>
              <span className="peptide-card-price-per-mg-value">{formattedPricePerMg}</span>
            </div>
          )}
        </div>

        {activeCoupon.code && (
          <div className="peptide-card-coupon">
            <div className="peptide-card-coupon-row">
              <span className="peptide-card-coupon-label">Coupon:</span>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(activeCoupon.code || '').then(() => {
                    // Could show a toast notification here
                    console.log('Coupon code copied:', activeCoupon.code);
                  }).catch((err) => {
                    console.warn('Failed to copy coupon code', err);
                  });
                }}
                className="peptide-card-coupon-code"
                title="Click to copy coupon code"
                aria-label={`Copy coupon code ${activeCoupon.code}`}
              >
                {activeCoupon.code}
              </button>
            </div>
            {activeCoupon.description && (
              <span className="peptide-card-coupon-desc">{activeCoupon.description}</span>
            )}
          </div>
        )}

        <footer className="peptide-card-footer">
          <a
            href={productUrl}
            onClick={(e) => {
              e.preventDefault();
              onPress();
            }}
            className="peptide-card-button"
            aria-label={`View ${peptideName} on ${product.vendor_name}`}
          >
            View Product
          </a>
        </footer>
      </div>
    </article>
  );
}

